import React, { useState, useMemo } from 'react';
import { Input, Select, Tag } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import type { SelectProps } from 'antd';
import Styles from './Styles.module.scss';
import cs from 'classnames';

const { Search } = Input;

interface MultiSelectWithSearchProps extends Omit<SelectProps, 'showSearch' | 'onSearch' | 'filterOption'> {
    onSearch?: (search: string) => void;
    requiresValues?: boolean;
    getLabelForValue?: (value: any) => string;
    searchPlaceholder?: string;
}

export const MultiSelectWithSearch: React.FC<MultiSelectWithSearchProps> = ({
    onSearch,
    requiresValues = false,
    getLabelForValue,
    searchPlaceholder = 'Search...',
    options = [],
    dropdownRender,
    ...restProps
}) => {
    const [localSearch, setLocalSearch] = useState<string>('');
    const [isClosingTag, setIsClosingTag] = useState<boolean>(false);

    // Filter options based on local search (for non-requiresValues fields)
    const filteredOptions = useMemo(() => {
        if (requiresValues || !localSearch) {
            return options;
        }
        const searchLower = localSearch.toLowerCase();
        return options.filter((option: any) => {
            const label = option?.label || option?.title || '';
            return String(label).toLowerCase().includes(searchLower);
        });
    }, [options, localSearch, requiresValues]);

    // Handle search input change
    const handleSearchChange = (value: string) => {
        setLocalSearch(value);
        if (requiresValues && onSearch) {
            onSearch(value);
        }
    };

    // Custom dropdown render with search bar at top
    const customDropdownRender = (menu: React.ReactElement) => {
        return (
            <div>
                {/* Search bar at the top */}
                <div style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>
                    <Search
                        placeholder={searchPlaceholder}
                        value={localSearch}
                        onChange={e => handleSearchChange(e.target.value)}
                        allowClear
                        prefix={<SearchOutlined />}
                        style={{ width: '100%' }}
                    />
                </div>
                {/* Original menu or custom dropdown */}
                {dropdownRender ? (
                    dropdownRender(menu)
                ) : (
                    <div style={{ maxHeight: '300px', overflowY: 'auto' }}>{menu}</div>
                )}
            </div>
        );
    };

    // Custom tag render to use getLabelForValue if provided
    const customTagRender = (props: any) => {
        const { label, value, closable, onClose } = props;
        const displayLabel = getLabelForValue ? getLabelForValue(value) : label;

        // Handle close event and prevent dropdown from opening
        const handleClose = (e?: React.MouseEvent<HTMLElement>) => {
            if (e) {
                e.preventDefault();
                e.stopPropagation();
                // Mark that we're closing a tag
                setIsClosingTag(true);
                // Reset flag after event processing
                setTimeout(() => setIsClosingTag(false), 0);
            }
            if (onClose) {
                onClose(e);
            }
        };

        return (
            <Tag
                closable={closable}
                onClose={handleClose}
                style={{ marginRight: 0, maxWidth: '150px', display: 'flex' }}
                onMouseDown={e => {
                    // Prevent mousedown on tag from opening dropdown
                    if (e.target && (e.target as HTMLElement).closest('.anticon-close')) {
                        e.preventDefault();
                        e.stopPropagation();
                    }
                }}
                onClick={e => {
                    // Prevent click on tag close icon from opening dropdown
                    if (e.target && (e.target as HTMLElement).closest('.anticon-close')) {
                        e.preventDefault();
                        e.stopPropagation();
                    }
                }}
            >
                <span
                    style={{
                        maxWidth: '90px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                    }}
                >
                    {displayLabel}
                </span>
            </Tag>
        );
    };

    // Handle dropdown visibility change to prevent opening when closing tag
    const handleDropdownVisibleChange = (open: boolean) => {
        if (isClosingTag && open) {
            // Prevent dropdown from opening if we're closing a tag
            return;
        }
        // Call original handler if provided
        if (restProps.onOpenChange) {
            restProps.onOpenChange(open);
        }
    };

    return (
        <Select
            {...restProps}
            showSearch={false} // Disable built-in search
            filterOption={false} // Disable built-in filtering
            options={filteredOptions}
            className={cs(Styles.multiSelectWithSearch, Styles.customSelect)}
            popupRender={customDropdownRender}
            tagRender={getLabelForValue ? customTagRender : restProps.tagRender}
            onOpenChange={handleDropdownVisibleChange}
        />
    );
};
